// Нормативные документы: СНиП, ГОСТ, СП, законы — справочник стандартов.
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { BookMarked, Plus, Search, ExternalLink, Download, Trash2 } from 'lucide-react';
import { normativeApi, getErrorMessage } from '@/api';
import { AppLayout, PageHeader } from '@/components/layout/AppLayout';
import { Spinner } from '@/components/ui/Spinner';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { EmptyState } from '@/components/ui/EmptyState';
import { useToast } from '@/components/ui/Toast';
import { useAuth } from '@/hooks/useAuth';
import { formatDate } from '@/lib/format';
import type { NormativeDocument, NormativeCategory } from '@/types';

const CATEGORIES: { key: NormativeCategory | 'all'; label: string }[] = [
  { key: 'all', label: 'Все' },
  { key: 'snip', label: 'СНиП' },
  { key: 'gost', label: 'ГОСТ' },
  { key: 'sp', label: 'СП' },
  { key: 'law', label: 'Законы' },
  { key: 'rd', label: 'РД' },
  { key: 'other', label: 'Прочее' },
];
const UPLOAD_CATS = CATEGORIES.filter((c) => c.key !== 'all') as { key: NormativeCategory; label: string }[];
const CAT_COLOR: Record<string, string> = {
  snip: 'var(--blue)', gost: 'var(--green)', sp: 'var(--accent)',
  law: 'var(--red)', rd: 'var(--yellow)', other: 'var(--text-muted)',
};

export function NormativePage() {
  const { can } = useAuth();
  const { toast } = useToast();
  const qc = useQueryClient();
  const [category, setCategory] = useState<NormativeCategory | 'all'>('all');
  const [search, setSearch] = useState('');
  const [addOpen, setAddOpen] = useState(false);

  const { data: counts } = useQuery({ queryKey: ['normative-counts'], queryFn: () => normativeApi.counts() });
  const { data: docs, isLoading } = useQuery({
    queryKey: ['normative', { category, search }],
    queryFn: () => normativeApi.list({
      ...(category !== 'all' ? { category } : {}), ...(search ? { search } : {}),
    }),
  });

  return (
    <AppLayout>
      <PageHeader title="Нормативные документы"
        subtitle={counts ? `${counts.total} стандартов в базе` : 'СНиП, ГОСТ, СП, законы'}
        actions={can('library', 'add') && (
          <Button variant="primary" onClick={() => setAddOpen(true)}><Plus size={16} /> Добавить</Button>
        )} />

      <div style={{ position: 'relative', marginBottom: 14, maxWidth: 420 }}>
        <Search size={16} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
        <input className="field__input" style={{ paddingLeft: 36 }} value={search}
          onChange={(e) => setSearch(e.target.value)} placeholder="Поиск по обозначению или названию…" />
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 18 }}>
        {CATEGORIES.map((c) => (
          <button key={c.key} onClick={() => setCategory(c.key)}
            style={{
              padding: '6px 14px', borderRadius: 16, fontSize: 13, cursor: 'pointer',
              border: '1px solid var(--border-bright)',
              background: category === c.key ? 'var(--accent)' : 'transparent',
              color: category === c.key ? '#1a1205' : 'var(--text-secondary)',
              fontWeight: category === c.key ? 500 : 400,
            }}>
            {c.label}{c.key !== 'all' && counts && counts[c.key] > 0 ? ` (${counts[c.key]})` : ''}
          </button>
        ))}
      </div>

      {isLoading ? <Spinner label="Загрузка…" /> :
        !docs || docs.length === 0 ? (
          <EmptyState icon={<BookMarked size={32} />} title="Документов нет"
            hint={search || category !== 'all' ? 'По фильтру ничего не найдено' : 'Добавьте нормативный документ'} />
        ) : (
          <div className="panel">
            <table className="data-table">
              <thead>
                <tr><th>Обозначение</th><th>Наименование</th><th>Категория</th><th>Введён</th><th>Статус</th><th></th></tr>
              </thead>
              <tbody>
                {docs.map((d: NormativeDocument) => (
                  <tr key={d.id}>
                    <td className="mono" style={{ fontWeight: 500 }}>{d.code}</td>
                    <td>{d.title}</td>
                    <td><span style={{ color: CAT_COLOR[d.category], fontSize: 13 }}>{d.category_display}</span></td>
                    <td className="mono">{d.effective_date ? formatDate(d.effective_date) : '—'}</td>
                    <td>
                      {d.is_current
                        ? <span style={{ color: 'var(--green)', fontSize: 13 }}>Действующий</span>
                        : <span style={{ color: 'var(--text-muted)', fontSize: 13 }}>Отменён</span>}
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                        {d.file_url && (
                          <a href={d.file_url} target="_blank" rel="noreferrer" title="Скачать файл" style={{ color: 'var(--accent)', display: 'inline-flex' }}><Download size={15} /></a>
                        )}
                        {d.url && (
                          <a href={d.url} target="_blank" rel="noreferrer" title="Источник" style={{ color: 'var(--blue)', display: 'inline-flex' }}><ExternalLink size={15} /></a>
                        )}
                        {can('library', 'delete') && (
                          <DeleteBtn id={d.id} onDone={() => { qc.invalidateQueries({ queryKey: ['normative'] }); qc.invalidateQueries({ queryKey: ['normative-counts'] }); toast('Удалено', 'success'); }} />
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

      <AddNormativeModal open={addOpen} onClose={() => setAddOpen(false)}
        onAdded={() => { qc.invalidateQueries({ queryKey: ['normative'] }); qc.invalidateQueries({ queryKey: ['normative-counts'] }); toast('Документ добавлен', 'success'); }}
        onError={(m) => toast(m, 'error')} />
    </AppLayout>
  );
}

function DeleteBtn({ id, onDone }: { id: number; onDone: () => void }) {
  const m = useMutation({ mutationFn: () => normativeApi.remove(id), onSuccess: onDone });
  return (
    <button onClick={() => m.mutate()} disabled={m.isPending} title="Удалить"
      style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', display: 'inline-flex' }}>
      <Trash2 size={15} />
    </button>
  );
}

function AddNormativeModal({ open, onClose, onAdded, onError }: {
  open: boolean; onClose: () => void; onAdded: () => void; onError: (m: string) => void;
}) {
  const [code, setCode] = useState('');
  const [title, setTitle] = useState('');
  const [category, setCategory] = useState<NormativeCategory>('sp');
  const [url, setUrl] = useState('');
  const [file, setFile] = useState<File | null>(null);

  const m = useMutation({
    mutationFn: () => normativeApi.create({ code, title, category, url }, file ?? undefined),
    onSuccess: () => { onClose(); reset(); onAdded(); },
    onError: (e) => onError(getErrorMessage(e)),
  });
  const reset = () => { setCode(''); setTitle(''); setCategory('sp'); setUrl(''); setFile(null); };

  return (
    <Modal open={open} onClose={onClose} title="Нормативный документ"
      footer={<>
        <Button onClick={onClose}>Отмена</Button>
        <Button variant="primary" disabled={!code || !title || m.isPending} onClick={() => m.mutate()}>
          {m.isPending ? 'Сохранение…' : 'Добавить'}
        </Button>
      </>}>
      <div className="field"><label className="field__label">Обозначение</label>
        <input className="field__input" value={code} onChange={(e) => setCode(e.target.value)} placeholder="СП 17.13330.2017" /></div>
      <div className="field"><label className="field__label">Наименование</label>
        <input className="field__input" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Кровли" /></div>
      <div className="field"><label className="field__label">Категория</label>
        <select className="field__select" value={category} onChange={(e) => setCategory(e.target.value as NormativeCategory)}>
          {UPLOAD_CATS.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
        </select></div>
      <div className="field"><label className="field__label">Ссылка на источник (необязательно)</label>
        <input className="field__input" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://docs.cntd.ru/..." /></div>
      <div className="field"><label className="field__label">Файл (необязательно)</label>
        <input type="file" className="field__input" onChange={(e) => setFile(e.target.files?.[0] ?? null)} /></div>
    </Modal>
  );
}
